// Copyright 2025, University of Maryland, All Rights Reserved
import { formatInTimeZone } from "date-fns-tz"

const DOC_TYPE_BADGE: Record<string, string> = {
  exam:       'bg-red-100 text-red-700',
  syllabus:   'bg-blue-100 text-blue-700',
  assignment: 'bg-purple-100 text-purple-700',
  quiz:       'bg-amber-100 text-amber-700',
  gradedata:  'bg-green-100 text-green-700',
}

interface AnalysisHeaderProps {
  filename?: string;
  originalFilename?: string;
  filenames?: string[];  // Array of document filenames for bulk analysis
  documentTypes?: string[];  // Parallel array of document types for bulk analysis
  documentType?: string;
  llmModel?: string;
  llmCost?: number;
  duration?: number;
  promptName?: string;
  analysisType?: string;
  promptType?: string;
  templateName?: string;
  templateType?: string;
  analyzedBy?: string;
  analysisDate?: string;
  documentCount?: number;  // Number of documents in bulk analysis
}

export function AnalysisHeader({
  filename,
  originalFilename,
  filenames,
  documentTypes,
  documentType,
  llmModel = 'Claude Sonnet 4',
  llmCost,
  duration,
  promptName,
  analysisType,
  promptType,
  templateName,
  templateType,
  analyzedBy,
  analysisDate,
  documentCount,
}: AnalysisHeaderProps) {
  // Determine if this is a bulk analysis
  const isBulkAnalysis = documentCount && documentCount > 1;
  const hasFilenames = filenames && filenames.length > 0;

  return (
    <div className="bg-white p-8 rounded-lg shadow-sm pdf-page-break-avoid">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h2 className="text-3xl font-bold mb-2 text-gray-900">✅ Analysis Complete!</h2>
          
          {/* Show document names for bulk analysis or single document */}
          {isBulkAnalysis && hasFilenames ? (
            <div className="mt-2">
              <p className="text-lg font-semibold text-gray-700 mb-2">
                📚 Bulk Analysis: {documentCount} Documents
              </p>
              <div className="border-b border-gray-200 pb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Documents Analyzed:</p>
                <ul className="space-y-1">
                  {filenames.map((name, index) => {
                    const dtype = documentTypes?.[index]
                    return (
                      <li key={index} className="text-sm text-gray-700 flex items-center gap-2">
                        <span className="text-gray-600 font-bold">{index + 1}.</span>
                        <span>{name}</span>
                        {dtype && (
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium capitalize ${DOC_TYPE_BADGE[dtype] ?? 'bg-gray-100 text-gray-600'}`}>
                            {dtype}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          ) : (
            <p className="text-xl text-gray-600">{filename || originalFilename}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-700">
            <span>🤖 {llmModel}</span>
            <span>💰 ${llmCost?.toFixed(4) || '0.0000'}</span>
            {/*{duration && <span>⏱️ {duration.toFixed(2)}s</span>}*/}
          </div>

          {/* Analysis Metadata */}
          {(analyzedBy || analysisDate) && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
              {analyzedBy && <span>👤 Analyzed by: <span className="font-medium text-gray-800">{analyzedBy}</span></span>}
              {analysisDate && (
                <span>📅 Analysis Date: <span className="font-medium text-gray-800">
                  {formatInTimeZone(new Date(analysisDate), 'America/New_York', 'MMM d, yyyy h:mm a')}
                </span></span>
              )}
            </div>
          )}

          {/* Configuration Details */}
          {(promptName || templateName) && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Analysis Configuration</h3>
              <div className="space-y-2 text-sm">
                {promptName && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">💬 Prompt:</span>
                    <span className="font-medium text-gray-800">{promptName}</span>
                    {analysisType && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                        {analysisType}
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                      {promptType}
                    </span>
                  </div>
                )}
                {templateName && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">📋 Template:</span>
                    <span className="font-medium text-gray-800">{templateName}</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                      {templateType}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <aside className="text-right ml-4" aria-label="Document information">
          <div className="bg-[#B0001C] text-white px-4 py-2 rounded-lg">
            <p className="text-sm opacity-90 mb-1">Document Type</p>
            <p className="text-lg font-semibold">{documentType?.toUpperCase()}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
